/* eslint-disable no-undef */
import { createAxios } from '../lib/axiosFactory';
import { AccountStatus } from '../lib/enums/accountStatus';
import { BillableItemIds } from '../lib/enums/billableItem';
import { BusinessRegion } from '../lib/enums/businessRegion';
import { ClientSenderPreference } from '../lib/enums/clientSenderPreference';
import { CustomDomainStatus } from '../lib/enums/customDomainStatus';
import {
  AddContactPermission,
  BulkUploadPermission,
  ContactExportPermission
} from '../lib/enums/clientPermissions';
import { ApiCredentialProvider } from '../lib/enums/apiCredentialProvider';
import {
  ApiCredentials,
  Client,
  OAuthClientCredentials
} from '../lib/interfaces/client';
import { errorParser } from '../lib/response/errorParser';
import { VuexLocalStorage } from '../lib/vuexLocalStorage';

const storage = new VuexLocalStorage('_CLIENT_');

export const state = function (): Partial<Client> {
  return {
    companyName: storage.get('companyName'),
    websiteUrl: storage.get('websiteUrl'),
    shopifyDomain: storage.get('shopifyDomain'),
    timeZone: storage.get('timeZone'),
    industry: storage.get('industry'),
    contactDatabaseSize: storage.get('contactDatabaseSize'),
    experienceLevel: storage.get('experienceLevel'),
    source: storage.get('source'),
    subscriptionStatus: storage.get('subscriptionStatus') as AccountStatus,
    numberLookupMinimumCost: +storage.get('numberLookupMinimumCost'),
    numberLookupUnitCost: +storage.get('numberLookupUnitCost'),
    subscriptionTrialEndAt:
      Number(storage.get('subscriptionTrialEndAt')) || undefined,
    subscriptionPlanId: storage.get(
      'subscriptionPlanId'
    ) as BillableItemIds.Plan,
    subscriptionSmsCreditsQuantity: +storage.get(
      'subscriptionSmsCreditsQuantity'
    ),
    clientSenderPreference: storage.get(
      'clientSenderPreference'
    ) as ClientSenderPreference,
    subscribersCounts: +storage.get('subscribersCounts'),
    subscriptionContactLimit: +storage.get('subscriptionContactLimit'),
    businessRegion: storage.get('businessRegion'),
    streetAddress: storage.get('streetAddress'),
    streetAddress2: storage.get('streetAddress2'),
    city: storage.get('city'),
    state: storage.get('state'),
    zipCode: storage.get('zipCode'),
    country: storage.get('country'),
    createdAt: storage.get('createdAt'),
    updatedAt: storage.get('updatedAt'),
    status: storage.get('status'),
    messageSendingPhoneNumber: storage.get('messageSendingPhoneNumber'),
    messageSendingPhoneNumberId: storage.get('messageSendingPhoneNumberId'),
    domain: storage.get('domain'),
    sourceAccountId: storage.get('sourceAccountId'),
    customerCarePhone: storage.get('customerCarePhone'),
    isTestClient: JSON.parse(storage.get('isTestClient')),
    permissions: JSON.parse(storage.get('permissions')),
    customDomain: JSON.parse(storage.get('customDomain')),
    oAuthClientCredentials: JSON.parse(storage.get('oAuthClientCredentials')),
    notifications: JSON.parse(storage.get('notifications')),
    creditAutoRecharge: JSON.parse(storage.get('creditAutoRecharge')),
    apiCredentials: JSON.parse(storage.get('apiCredentials'))
  };
};

export const getters = {
  getOAuthClientCredentials: (state: any): OAuthClientCredentials => {
    return state.oAuthClientCredentials;
  },

  hasCustomDomain: (state: any, getters): boolean => {
    return !!getters.getCustomDomain;
  },

  hasMessageSendingPhoneNumber: (state: any): boolean => {
    return !!state.messageSendingPhoneNumber;
  },

  getCustomDomain: (state: any): string | undefined => {
    if (state.customDomain?.status === CustomDomainStatus.Connected)
      return state.customDomain.name;
    return undefined;
  },

  client: (state: Client): Client => {
    return state;
  },

  clientSource: (state: Client): string | null | undefined => {
    return state.source;
  },

  campaignUrlFieldPlaceHolder(state: Client, getters): string {
    const domain =
      getters.getCustomDomain ?? state.domain ?? `https://view.mobz.ly`;
    return `${domain}/s/xxxxx`;
  },

  isMobizClient: (state: Client): boolean => {
    return state.source === 'Mobiz';
  },

  isTelkomClient: (state: Client): boolean => {
    return state.source === 'Telkom';
  },

  isAppSumoClient: (state: Client): boolean => {
    return state.source === 'AppSumo';
  },

  isShopifyClient: (state: Client): boolean => {
    return state.source === 'Shopify';
  },

  isTelkomOrAppSumoClient: (state: Client, getters: any): boolean => {
    return getters.isTelkomClient || getters.isAppSumoClient;
  },

  isTrialClient: (state: Client): boolean => {
    return (
      state.subscriptionStatus === AccountStatus.Trial ||
      state.subscriptionPlanId ===
        BillableItemIds.Plan.TrialPackageUsdMonthly ||
      state.subscriptionPlanId ===
        BillableItemIds.Plan.ClosedBetaPackageUsdMonthly
    );
  },

  isFreePlan: (state: Client): boolean => {
    return state.subscriptionPlanId === BillableItemIds.Plan.FreeUsdMonthly;
  },

  subscriptionStatus: (state: Client): string => {
    return state.subscriptionStatus;
  },

  isActivePaidClient: (state: Client): boolean => {
    return !!(
      state.subscriptionPlanId &&
      [AccountStatus.Active, AccountStatus.Created].includes(
        state.subscriptionStatus
      )
    );
  },
  hasTriggerCampaignApiPermissions: (state: Client): boolean => {
    return state.subscriptionStatus === AccountStatus.Active;
  },

  isOptInRequired: (state: Client): boolean => {
    return (
      state.permissions.addContactVerification.value ===
      AddContactPermission.OptInRequired
    );
  },

  isMessageToNewContactsRequired: (state: Client): boolean => {
    return (
      state.permissions.addContactVerification.value !==
      AddContactPermission.NoRequest
    );
  },

  isActiveClient: (state: Client, getters: any): boolean => {
    return !!(getters.isInTrial || getters.isActivePaidClient);
  },

  subscriptionTrialEndAt: (state: Client): number => {
    return state.subscriptionTrialEndAt;
  },

  hasTrialExpired: (state: Client, getter): boolean => {
    const currentDate = Date.now() / 1000;

    return getter.isTrialClient && currentDate > state.subscriptionTrialEndAt;
  },

  isInTrial: (state: Client, getter): boolean => {
    const currentDate = Date.now() / 1000;

    return getter.isTrialClient && currentDate < state.subscriptionTrialEndAt;
  },

  requiresContactVerification: (state: Client, getters): boolean => {
    const isOptInRequired = getters.hasPermission(
      AddContactPermission.OptInRequired
    );
    const isOptOutRequired = getters.hasPermission(
      AddContactPermission.OptOutRequired
    );
    const hasVerificationMessageFields = !!(
      state.companyName && state.customerCarePhone
    );
    return (
      (isOptOutRequired || isOptInRequired) && !hasVerificationMessageFields
    );
  },
  hasCompanyAndCustomerCarePhone: (state: Client, getters): boolean => {
    return !!(state.companyName && state.customerCarePhone);
  },
  hasPermission: (state: Client) => (
    permission:
      | AddContactPermission
      | BulkUploadPermission
      | ContactExportPermission
  ): boolean => {
    return Object.values(state.permissions).some(x => x.value === permission);
  },

  status: (state: Client): string => {
    return state.status;
  },

  hasReachedContactLimit: (
    state: Client,
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    _getter,
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    rootState
  ): boolean => {
    return (
      state.subscriptionContactLimit <= rootState.contact.totalSubscribedCount
    );
  },

  hasLessThanCredits: (
    state: Client,
    _getter,
    rootState
  ): ((num: number) => boolean) => {
    return num => {
      return (
        Number.isInteger(rootState.user.creditsAvailable) &&
        rootState.user.creditsAvailable <= num
      );
    };
  },

  has0Credits: (state: Client, _getter, rootState): boolean => {
    return rootState.user.creditsAvailable === 0;
  },

  isScheduledToBeCancelled: (
    state: Client,
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    _getters,
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    rootState
  ): boolean => {
    const cancelledAt = rootState.subscription.subscription?.cancelledAt;
    return cancelledAt && cancelledAt > +new Date() / 1000;
  },

  isInternationalClient: (state: Client): boolean => {
    return state.businessRegion === BusinessRegion.International;
  },

  isUSCanadaClient: (state: Client): boolean => {
    return state.businessRegion === BusinessRegion.UnitedStatesCanada;
  },

  shortenedLinkDomain(state: Client, getters) {
    return getters.getCustomDomain || state.domain;
  }
};

export const mutations = {
  // eslint-disable-next-line @typescript-eslint/ban-types
  setCompanyName(state: Client, payload: object): void {
    storage.set(state, payload, 'companyName');
  },

  setOAuthClientCredentials(
    state: Client,
    payload: OAuthClientCredentials
  ): void {
    storage.set(state, payload, 'oAuthClientCredentials', true);
  },

  setApiCredentials(state: Client, payload: ApiCredentials) {
    storage.set(state, payload, 'apiCredentials', true);
    state.apiCredentials = [
      {
        provider: ApiCredentialProvider.KeyVault,
        apiKey: payload.apiCredentials.apiKey
      }
    ];
  },

  setWebsiteUrl(state: Client, payload: { websiteUrl: string }): void {
    storage.set(state, payload, 'websiteUrl');
  },

  setShopifyDomain(state: Client, payload: { shopifyDomain: string }): void {
    storage.set(state, payload, 'shopifyDomain');
  },

  setSubscribersCounts(
    state: Client,
    payload: { subscribersCounts: number }
  ): void {
    storage.set(state, payload, 'subscribersCounts');
  },

  setTimeZone(state: Client, payload: { timeZone: string }): void {
    storage.set(state, payload, 'timeZone');
  },

  setIndustry(state: Client, payload: { industry: string }): void {
    storage.set(state, payload, 'industry');
  },

  setContactDatabaseSize(
    state: Client,
    payload: { contactDatabaseSize: string }
  ): void {
    storage.set(state, payload, 'contactDatabaseSize');
  },

  setExperienceLevel(
    state: Client,
    payload: { experienceLevel: string }
  ): void {
    storage.set(state, payload, 'experienceLevel');
  },

  clearSession(state: Client): void {
    storage.clear(state);
  },

  setSource(state: Client, payload: { source: string }): void {
    storage.set(state, payload, 'source');
  },

  setClientSenderPreference(
    state: Client,
    payload: { clientSenderPreference: string }
  ): void {
    storage.set(state, payload, 'clientSenderPreference');
  },

  setSubscriptionStatus(
    state: Client,
    payload: { subscriptionStatus: string }
  ): void {
    storage.set(state, payload, 'subscriptionStatus');
  },

  setSubscriptionTrialEndAt(
    state: Client,
    payload: { subscriptionTrialEndAt: string }
  ): void {
    storage.set(state, payload, 'subscriptionTrialEndAt');
  },

  setSubscriptionPlanId(
    state: Client,
    payload: { subscriptionPlanId: string }
  ): void {
    storage.set(state, payload, 'subscriptionPlanId');
  },

  setSubscriptionSmsCreditsQuantity(
    state: Client,
    payload: { subscriptionSmsCreditsQuantity: string }
  ): void {
    storage.set(state, payload, 'subscriptionSmsCreditsQuantity');
  },

  setSubscriptionContactLimit(
    state: Client,
    payload: { subscriptionContactLimit: string }
  ): void {
    storage.set(state, payload, 'subscriptionContactLimit');
  },

  setBusinessRegion(state: Client, payload: { businessRegion: string }): void {
    storage.set(state, payload, 'businessRegion');
  },

  setCreatedAt(state: Client, payload: { createdAt: number }): void {
    storage.set(state, payload, 'createdAt');
  },

  setUpdatedAt(state: Client, payload: { updatedAt: number }): void {
    storage.set(state, payload, 'updatedAt');
  },

  setStreetAddress(state, payload: { streetAddress: string }): void {
    storage.set(state, payload, 'streetAddress');
  },

  setStreetAddress2(state, payload: { streetAddress2: string }): void {
    storage.set(state, payload, 'streetAddress2');
  },

  setCity(state, payload: { city: string }): void {
    storage.set(state, payload, 'city');
  },

  setState(state, payload: { state: string }): void {
    storage.set(state, payload, 'state');
  },

  setZipCode(state, payload: { zipCode: string }): void {
    storage.set(state, payload, 'zipCode');
  },

  setCountry(state, payload: { country: string }): void {
    storage.set(state, payload, 'country');
  },

  setStatus(state: Client, payload: { status: string }): void {
    storage.set(state, payload, 'status');
  },

  setMessageSendingPhoneNumber(
    state: Client,
    payload: { messageSendingPhoneNumber: string }
  ): void {
    storage.set(state, payload, 'messageSendingPhoneNumber');
  },

  setMessageSendingPhoneNumberId(
    state: Client,
    payload: { messageSendingPhoneNumberId: string }
  ): void {
    storage.set(state, payload, 'messageSendingPhoneNumberId');
  },

  setDomain(state: Client, payload: { domain: string }): void {
    storage.set(state, payload, 'domain');
  },

  setSourceAccountId(
    state: Client,
    payload: { sourceAccountId: string }
  ): void {
    storage.set(state, payload, 'sourceAccountId');
  },

  setCustomerCarePhone(
    state: Client,
    payload: { customerCarePhone: string }
  ): void {
    storage.set(state, payload, 'customerCarePhone');
  },

  setIsTestClient(state: Client, payload: { isTestClient: boolean }): void {
    storage.set(state, payload, 'isTestClient');
  },

  setPermissions(state: Client, payload: { permissions: string }): void {
    storage.set(state, payload, 'permissions', true);
  },

  setCompanyLogoUrl(state: Client, payload): void {
    state.companyLogoUrl = payload.companyLogoUrl;
    storage.set(state, payload, 'companyLogoUrl');
  },

  setVCardUrl(state: Client, payload: { vCardUrl: string }): void {
    storage.set(state, payload, 'vCardUrl');
  },

  setClient(state: Client, payload: any): void {
    storage.set(
      state,
      { companyName: payload.attributes.companyName },
      'companyName'
    );
    storage.set(
      state,
      { websiteUrl: payload.attributes.websiteUrl },
      'websiteUrl'
    );

    storage.set(
      state,
      { shopifyDomain: payload.attributes.shopifyDomain },
      'shopifyDomain'
    );

    storage.set(
      state,
      { companyLogoUrl: payload.attributes.companyLogoUrl },
      'companyLogoUrl'
    );

    storage.set(state, { timeZone: payload.attributes.timeZone }, 'timeZone');
    storage.set(state, { industry: payload.attributes.industry }, 'industry');
    storage.set(state, { source: payload.attributes.source }, 'source');
    storage.set(
      state,
      { contactDatabaseSize: payload.attributes.contactDatabaseSize },
      'contactDatabaseSize'
    );
    storage.set(
      state,
      { experienceLevel: payload.attributes.experienceLevel },
      'experienceLevel'
    );
    if (payload.attributes.subscriptionStatus)
      storage.set(
        state,
        { subscriptionStatus: payload.attributes.subscriptionStatus },
        'subscriptionStatus'
      );
    if (payload.attributes.subscriptionTrialEndAt)
      storage.set(
        state,
        {
          subscriptionTrialEndAt: payload.attributes.subscriptionTrialEndAt
        },
        'subscriptionTrialEndAt'
      );
    if (payload.attributes.subscriptionPlanId)
      storage.set(
        state,
        { subscriptionPlanId: payload.attributes.subscriptionPlanId },
        'subscriptionPlanId'
      );
    if (payload.attributes.clientSenderPreference) {
      storage.set(
        state,
        {
          clientSenderPreference: payload.attributes.clientSenderPreference
        },
        'clientSenderPreference'
      );
    }
    if (payload.attributes.subscriptionSmsCreditsQuantity)
      storage.set(
        state,
        {
          subscriptionSmsCreditsQuantity:
            payload.attributes.subscriptionSmsCreditsQuantity
        },
        'subscriptionSmsCreditsQuantity'
      );
    if (payload.attributes.subscriptionContactLimit)
      storage.set(
        state,
        {
          subscriptionContactLimit: payload.attributes.subscriptionContactLimit
        },
        'subscriptionContactLimit'
      );
    if (payload.attributes.customDomain)
      storage.set(
        state,
        { customDomain: payload.attributes.customDomain },
        'customDomain'
      );
    storage.set(
      state,
      { businessRegion: payload.attributes.businessRegion },
      'businessRegion'
    );

    storage.set(
      state,
      { streetAddress: payload.attributes.streetAddress },
      'streetAddress'
    );
    storage.set(
      state,
      { streetAddress2: payload.attributes.streetAddress2 },
      'streetAddress2'
    );
    storage.set(state, { city: payload.attributes.city }, 'city');
    storage.set(state, { state: payload.attributes.state }, 'state');
    storage.set(state, { zipCode: payload.attributes.zipCode }, 'zipCode');
    storage.set(state, { country: payload.attributes.country }, 'country');
    storage.set(
      state,
      { createdAt: payload.attributes.createdAt },
      'createdAt'
    );
    storage.set(
      state,
      { updatedAt: payload.attributes.updatedAt },
      'updatedAt'
    );
    storage.set(state, { status: payload.attributes.status }, 'status');
    storage.set(
      state,
      {
        messageSendingPhoneNumber: payload.attributes.messageSendingPhoneNumber
      },
      'messageSendingPhoneNumber'
    );
    storage.set(
      state,
      {
        messageSendingPhoneNumberId:
          payload.attributes.messageSendingPhoneNumberId
      },
      'messageSendingPhoneNumberId'
    );
    storage.set(
      state,
      {
        domain: payload.attributes.domain
      },
      'domain'
    );
    storage.set(
      state,
      { sourceAccountId: payload.attributes.sourceAccountId },
      'sourceAccountId'
    );
    storage.set(
      state,
      { customerCarePhone: payload.attributes.customerCarePhone },
      'customerCarePhone'
    );
    storage.set(
      state,
      { isTestClient: !!payload.attributes.isTestClient },
      'isTestClient'
    );
    storage.set(
      state,
      { permissions: payload.attributes.permissions },
      'permissions',
      true
    );
    storage.set(
      state,
      { customDomain: payload.attributes?.customDomain },
      'customDomain',
      true
    );

    storage.set(
      state,
      { notifications: payload.attributes?.notifications },
      'notifications',
      true
    );

    if (payload.attributes.numberLookupMinimumCost != null)
      storage.set(
        state,
        {
          numberLookupMinimumCost: payload.attributes.numberLookupMinimumCost
        },
        'numberLookupMinimumCost'
      );

    if (payload.attributes.numberLookupUnitCost != null)
      storage.set(
        state,
        {
          numberLookupUnitCost: payload.attributes.numberLookupUnitCost
        },
        'numberLookupUnitCost'
      );
  },

  setCustomDomain(
    state: any,
    payload: { customDomain: Record<string, any> }
  ): void {
    storage.set(state, payload, 'customDomain', true);
  },

  setNotifications(
    state: Client,
    payload: { notifications: Record<string, any> }
  ): void {
    storage.set(state, payload, 'notifications', true);
  },

  setCreditAutoRecharge(
    state: Client,
    payload: { creditAutoRecharge: Record<string, any> }
  ): void {
    storage.set(state, payload, 'creditAutoRecharge', true);
  }
};

export const actions = {
  async getClientDetails({ commit, state }) {
    try {
      const response = await createAxios().get('backend/clients-show');

      commit('setCompanyName', {
        companyName: response.data.data.attributes.companyName
      });
      commit('setBusinessRegion', {
        businessRegion: response.data.data.attributes.businessRegion
      });
      commit('setWebsiteUrl', {
        websiteUrl: response.data.data.attributes.websiteUrl
      });
      commit('setStreetAddress', {
        streetAddress: response.data.data.attributes.streetAddress
      });
      commit('setShopifyDomain', {
        shopifyDomain: response.data.data.attributes.shopifyDomain
      });
      commit('setStreetAddress2', {
        streetAddress2: response.data.data.attributes.streetAddress2
      });
      commit('setCity', {
        city: response.data.data.attributes.city
      });
      commit('setState', {
        state: response.data.data.attributes.state
      });
      commit('setZipCode', {
        zipCode: response.data.data.attributes.zipCode
      });
      commit('setCountry', {
        country: response.data.data.attributes.country
      });
      commit('setTimeZone', {
        timeZone: response.data.data.attributes.timeZone
      });
      commit('setIndustry', {
        industry: response.data.data.attributes.industry
      });
      commit('setSource', {
        source: response.data.data.attributes.source
      });
      commit('setMessageSendingPhoneNumber', {
        messageSendingPhoneNumber:
          response.data.data.attributes.messageSendingPhoneNumber
      });
      commit('setMessageSendingPhoneNumberId', {
        messageSendingPhoneNumberId:
          response.data.data.attributes.messageSendingPhoneNumberId
      });
      commit('setDomain', {
        domain: response.data.data.attributes.domain
      });
      commit('setCreatedAt', {
        createdAt: response.data.data.attributes.createdAt
      });
      commit('setUpdatedAt', {
        updatedAt: response.data.data.attributes.updatedAt
      });
      commit('setStatus', {
        status: response.data.data.attributes.status
      });
      commit('setContactDatabaseSize', {
        contactDatabaseSize: response.data.data.attributes.contactDatabaseSize
      });
      commit('setClientSenderPreference', {
        clientSenderPreference:
          response.data.data.attributes.clientSenderPreference
      });
      commit(
        'feature/setSubscriptionFeatures',
        {
          subscriptionFeatures:
            response.data.data.attributes.subscriptionFeatures
        },
        { root: true }
      );
      commit('setCustomerCarePhone', {
        customerCarePhone: response.data.data.attributes.customerCarePhone
      });
      commit('setIsTestClient', {
        isTestClient: !!response.data.data.attributes.isTestClient
      });
      commit('setCustomDomain', {
        customDomain: response.data.data.attributes.customDomain
      });
      if (response.data.data.attributes?.oAuthClientCredentials) {
        commit('setOAuthClientCredentials', {
          oAuthClientCredentials: {
            clientSecret:
              response.data.data.attributes.oAuthClientCredentials.clientSecret,
            auth0ClientId:
              response.data.data.attributes.oAuthClientCredentials.auth0ClientId
          }
        });
      } else {
        commit('setOAuthClientCredentials', {
          oAuthClientCredentials: undefined
        });
      }

      if (response.data.data.attributes?.apiCredentials) {
        const apiKeyCredentials = response.data.data.attributes.apiCredentials.find(
          (cred: any) => cred.provider === ApiCredentialProvider.KeyVault
        );

        commit('setApiCredentials', {
          apiCredentials: {
            apiKey: apiKeyCredentials ? apiKeyCredentials.apiKey : undefined
          }
        });
      } else {
        commit('setApiCredentials', {
          apiCredentials: undefined
        });
      }

      commit('setNotifications', {
        notifications: response.data.data.attributes?.notifications
      });

      commit('setCreditAutoRecharge', {
        creditAutoRecharge: response.data.data.attributes?.creditAutoRecharge
      });
      commit('setCompanyLogoUrl', {
        companyLogoUrl: response.data.data.attributes?.companyLogoUrl
      });

      commit('setVCardUrl', {
        vCardUrl: response.data.data.attributes?.vCardUrl
      });

      return { success: true, errors: {}, body: response.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async loadNumberOfSubscribers({ commit, state }) {
    try {
      const response = await createAxios().get(
        'backend/subscribers-counts-show'
      );

      commit('setSubscribersCounts', {
        subscribersCounts: response.data.data.attributes.subscribersCounts
      });
      return { success: true, errors: {}, body: response.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async updateClients({ commit }: any, payload: any) {
    console.log('Payload', payload);
    try {
      const response = await createAxios().patch('backend/clients-update', {
        data: {
          type: 'clients',
          attributes: payload
        }
      });

      commit('setCompanyName', {
        companyName: response.data.data.attributes.companyName
      });
      commit('setBusinessRegion', {
        businessRegion: response.data.data.attributes.businessRegion
      });
      commit('setWebsiteUrl', {
        websiteUrl: response.data.data.attributes.websiteUrl
      });
      commit('setStreetAddress', {
        streetAddress: response.data.data.attributes.streetAddress
      });
      commit('setShopifyDomain', {
        shopifyDomain: response.data.data.attributes.shopifyDomain
      });
      commit('setStreetAddress2', {
        streetAddress2: response.data.data.attributes.streetAddress2
      });
      commit('setCity', {
        city: response.data.data.attributes.city
      });
      commit('setState', {
        state: response.data.data.attributes.state
      });
      commit('setZipCode', {
        zipCode: response.data.data.attributes.zipCode
      });
      commit('setCountry', {
        country: response.data.data.attributes.country
      });
      commit('setTimeZone', {
        timeZone: response.data.data.attributes.timeZone
      });
      commit('setIndustry', {
        industry: response.data.data.attributes.industry
      });
      commit('setSource', {
        source: response.data.data.attributes.source
      });
      commit('setMessageSendingPhoneNumber', {
        messageSendingPhoneNumber:
          response.data.data.attributes.messageSendingPhoneNumber
      });
      commit('setMessageSendingPhoneNumberId', {
        messageSendingPhoneNumberId:
          response.data.data.attributes.messageSendingPhoneNumberId
      });
      commit('setDomain', {
        domain: response.data.data.attributes.domain
      });
      commit('setCreatedAt', {
        createdAt: response.data.data.attributes.createdAt
      });
      commit('setUpdatedAt', {
        updatedAt: response.data.data.attributes.updatedAt
      });
      commit('setStatus', {
        status: response.data.data.attributes.status
      });
      commit('setContactDatabaseSize', {
        contactDatabaseSize: response.data.data.attributes.contactDatabaseSize
      });
      commit('setClientSenderPreference', {
        clientSenderPreference:
          response.data.data.attributes.clientSenderPreference
      });
      commit(
        'feature/setSubscriptionFeatures',
        {
          subscriptionFeatures:
            response.data.data.attributes.subscriptionFeatures
        },
        { root: true }
      );
      commit('setCustomerCarePhone', {
        customerCarePhone: response.data.data.attributes.customerCarePhone
      });
      commit('setIsTestClient', {
        isTestClient: !!response.data.data.attributes.isTestClient
      });
      commit('setCustomDomain', {
        customDomain: response.data.data.attributes.customDomain
      });

      commit('setNotifications', {
        notifications: response.data.data.attributes.notifications
      });

      commit('setPermissions', {
        permissions: response.data.data.attributes.permissions
      });

      commit('setCompanyLogoUrl', {
        companyLogoUrl: response.data.data.attributes?.companyLogoUrl
      });
      commit('setVCardUrl', {
        vCardUrl: response.data.data.attributes?.vCardUrl
      });

      return { success: true, errors: {}, body: {} };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async verifyCustomDomain({ commit }: any) {
    try {
      const response = await createAxios().get(
        'backend/clients-custom-domain-verify'
      );

      commit('setCustomDomain', {
        customDomain: response.data.data.attributes.customDomain
      });

      return {
        success: true,
        errors: {},
        body: {
          customDomain: response.data.data.attributes.customDomain
        }
      };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async createClientCredentials({ commit }: any, preferApiKey = true) {
    try {
      const response = await createAxios().post(
        'backend/client-credentials-create?preferApiKey=' + preferApiKey,
        {}
      );

      commit('setOAuthClientCredentials', {
        oAuthClientCredentials: {
          clientSecret: response.data.data.attributes.clientSecret,
          auth0ClientId: response.data.data.attributes.applicationClientId
        }
      });

      commit('setApiCredentials', {
        apiCredentials: {
          apiKey: response.data.data.attributes.apiKey
        }
      });

      return {
        success: true,
        errors: {},
        body: response.data.data
      };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response?.data
      };
    }
  },

  async update({ commit }: any, payload: any) {
    try {
      const response = await createAxios().patch('backend/clients-update', {
        data: {
          type: 'clients',
          attributes: payload
        }
      });

      return {
        success: true,
        errors: {},
        body: response.data.data.attributes
      };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },
  async autoRechargeRetry({ commit }: any, payload: any) {
    try {
      const response = await createAxios().post(
        'backend/credit-auto-recharge-retry-create'
      );

      commit('setNotifications', {
        notifications: response.data.data.attributes.notifications
      });

      return {
        success: true,
        errors: {},
        body: response.data.data.attributes
      };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  }
};
